'use client';

import { Temporal as TemporalPolyfill } from 'temporal-polyfill';

declare global {
  var Temporal: typeof TemporalPolyfill;
}

if (typeof globalThis.Temporal === 'undefined') {
  globalThis.Temporal = TemporalPolyfill;
}

import {
  type CalendarEventExternal,
  createViewDay,
  createViewList,
  createViewMonthGrid,
  createViewWeek,
} from '@schedule-x/calendar';
import { ScheduleXCalendar, useCalendarApp } from '@schedule-x/react';
import { useTheme } from 'next-themes';
import { type RefObject, useEffect, useMemo, useRef, useState } from 'react';
import '@schedule-x/theme-default/dist/index.css';
import './calendar-view-overrides.css';
import { cn } from '../../lib/utils';
import { UserAvatar, type UserProfileSummary } from '../../ui/user-avatar';

export interface CalendarEvent {
  id: string | number;
  title: string;
  start: CalendarEventExternal['start'];
  end: CalendarEventExternal['end'];
  assignedUser?: UserProfileSummary | null;
}

function CalendarEventCard({ calendarEvent }: { calendarEvent: CalendarEventExternal }) {
  const assignedUser = (calendarEvent as { assignedUser?: UserProfileSummary | null }).assignedUser;
  return (
    <div className="flex items-center justify-between gap-1.5 w-full h-full px-2 py-0.5 overflow-hidden text-xs rounded font-medium">
      <span className="truncate flex-1">{calendarEvent.title}</span>
      {assignedUser && (
        <UserAvatar
          user={assignedUser}
          size="xs"
          showTooltip
          className="shrink-0 h-4 w-4 text-[9px]"
        />
      )}
    </div>
  );
}

// Hoisted rather than written inline at the render site: ScheduleXCalendar lists
// `customComponents` in the dependencies of the effect that calls `calendarApp.render()`, so a
// fresh object each render would tear down and re-render the whole calendar every time.
// No entry for the list view — schedule-x exposes no custom-component slot for it (see
// `CustomComponentName`), so its rows render schedule-x's own markup and carry no assignee
// avatar.
const CUSTOM_COMPONENTS = {
  monthGridEvent: CalendarEventCard,
  dateGridEvent: CalendarEventCard,
  timeGridEvent: CalendarEventCard,
};

/**
 * Heights in px of the pieces schedule-x stacks inside a month-grid day cell, read off
 * @schedule-x/theme-default. `HEADER` is the taller first-week header (weekday name + date),
 * so a capacity derived from these is safe for every row, not just the ones below it.
 */
const MONTH_DAY_PADDING_Y = 16;
const MONTH_DAY_HEADER = 45;
const MONTH_EVENT_HEIGHT = 20;
const MONTH_EVENT_GAP = 4;
const MONTH_MORE_LINK_HEIGHT = 30;
const MONTH_MAX_EVENTS_PER_DAY = 10;
/** schedule-x's own default, used for the first paint until a row has been measured. */
const MONTH_DEFAULT_EVENTS_PER_DAY = 4;

/**
 * How many events a month-grid day can show inside a week row `rowHeight` px tall. Space for
 * the "+ N more" link is always reserved, so a day with more events than fit collapses the
 * remainder into that link rather than growing its row and breaking the equal-height layout.
 */
function monthEventsPerRow(rowHeight: number): number {
  const usable = rowHeight - MONTH_DAY_PADDING_Y - MONTH_DAY_HEADER - MONTH_MORE_LINK_HEIGHT;
  const fits = Math.floor((usable + MONTH_EVENT_GAP) / (MONTH_EVENT_HEIGHT + MONTH_EVENT_GAP));
  return Math.min(MONTH_MAX_EVENTS_PER_DAY, Math.max(1, fits));
}

interface CalendarViewProps {
  events: CalendarEvent[];
  /**
   * View schedule-x opens on. schedule-x has no callback for its own view-selection
   * dropdown (unlike date navigation, see `onSelectedDateUpdate` below), so that dropdown is
   * hidden here — drive the view externally (e.g. `CalendarViewSwitcher`) and remount this
   * component (via a `key` derived from the view) to change it after mount.
   */
  defaultView?: 'day' | 'week' | 'month-grid' | 'list';
  /**
   * Date the calendar should open on. schedule-x only reads this at mount time (see the
   * `events` note below), so callers that need to jump to a new date after mount must
   * remount this component (e.g. via a `key` derived from the date).
   */
  selectedDate?: TemporalPolyfill.PlainDate;
  /** Fires whenever the calendar's visible date changes — user prev/next/today navigation, clicking a date, switching views, etc. Use to mirror the date back into the URL for two-way sync. */
  onSelectedDateUpdate?: (date: TemporalPolyfill.PlainDate) => void;
  onEventClick?: (event: CalendarEventExternal) => void;
  className?: string;
}

type ScheduleXView = ReturnType<
  typeof createViewDay | typeof createViewWeek | typeof createViewMonthGrid | typeof createViewList
>;

interface ScheduleXHostProps extends CalendarViewProps {
  /** The box the calendar fills, owned by `CalendarView` so it survives this component's remounts. */
  containerRef: RefObject<HTMLDivElement | null>;
  eventsPerDay: number;
  onMeasureEventsPerDay: (eventsPerDay: number) => void;
}

function ScheduleXHost({
  events,
  defaultView = 'month-grid',
  selectedDate,
  onSelectedDateUpdate,
  onEventClick,
  containerRef,
  eventsPerDay,
  onMeasureEventsPerDay,
}: ScheduleXHostProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  // The Agenda tab uses schedule-x's `list` view — a flat chronological run of days, each
  // with its events, like Google Calendar's Schedule view — and not `month-agenda`, which is
  // a mini month grid with a single day's events beneath it and is flagged
  // `hasWideScreenCompat: false`, so on any calendar wider than 700px schedule-x silently
  // swapped it for the Day grid while the switcher still read "Agenda".
  const views = useMemo(
    () =>
      [createViewDay(), createViewWeek(), createViewMonthGrid(), createViewList()] as [
        ScheduleXView,
        ...ScheduleXView[],
      ],
    []
  );

  const calendar = useCalendarApp({
    views,
    events,
    defaultView,
    selectedDate,
    isDark,
    monthGridOptions: { nEventsPerDay: eventsPerDay },
    callbacks: {
      onEventClick: (event) => onEventClick?.(event),
      onSelectedDateUpdate: (date) => onSelectedDateUpdate?.(date),
    },
  });

  useEffect(() => {
    if (calendar) {
      calendar.setTheme(isDark ? 'dark' : 'light');
    }
  }, [isDark, calendar]);

  // useCalendarApp only reads `events` on the initial mount (see @schedule-x/react's
  // useCalendarApp, which creates the app inside a `useEffect(..., [])`), so events
  // arriving after mount (e.g. once an async task query resolves) must be pushed in
  // via the app's own events facade rather than relying on the config prop.
  useEffect(() => {
    if (calendar) {
      calendar.events.set(events);
    }
  }, [events, calendar]);

  // Re-measure the month grid once schedule-x has rendered it and whenever the box resizes,
  // so `nEventsPerDay` always matches what a row can actually show. Depending on `calendar`
  // is what makes the first pass work: @schedule-x/react only paints the grid in an effect
  // once the app instance exists, so nothing is in the DOM to measure before then. Rows are
  // counted from the DOM rather than derived from the date because how many week rows a
  // month spans depends on schedule-x's `firstDayOfWeek`.
  useEffect(() => {
    const container = containerRef.current;
    if (!calendar || !container || defaultView !== 'month-grid') return;

    const measure = () => {
      const grid = container.querySelector<HTMLElement>('.sx__month-grid-wrapper');
      const rowCount = container.querySelectorAll('.sx__month-grid-week').length;
      if (!grid || rowCount === 0) return;
      onMeasureEventsPerDay(monthEventsPerRow(grid.clientHeight / rowCount));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [calendar, defaultView, containerRef, onMeasureEventsPerDay]);

  return <ScheduleXCalendar calendarApp={calendar} customComponents={CUSTOM_COMPONENTS} />;
}

export function CalendarView({ className, ...props }: CalendarViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [eventsPerDay, setEventsPerDay] = useState(MONTH_DEFAULT_EVENTS_PER_DAY);

  return (
    <div
      ref={containerRef}
      className={cn(
        'w-full h-full bg-card rounded-xl shadow-xs border border-border/50 overflow-hidden',
        className
      )}
    >
      {/*
        schedule-x reads `monthGridOptions` once, when the calendar app is created, so a change
        in how many events fit a row has to remount it — the same remount-to-reconfigure
        pattern CalendarPage already uses for `defaultView` and `selectedDate`. Settling on a
        value takes at most one extra mount, since the remeasure after the remount reports the
        same number back.
      */}
      <ScheduleXHost
        {...props}
        key={eventsPerDay}
        containerRef={containerRef}
        eventsPerDay={eventsPerDay}
        onMeasureEventsPerDay={setEventsPerDay}
      />
    </div>
  );
}
